import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Minus, Plus, ShoppingBag, Download, Loader2, BadgeCheck } from "lucide-react";
import api, { money, FALLBACK_IMG, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import Seo from "@/components/Seo";
import Stars from "@/components/Stars";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState({ average: 0, count: 0, reviews: [] });
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadReviews = () => api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${slug}`).then((r) => setProduct(r.data)).catch(() => navigate("/shop"));
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate]);

  const submitReview = async () => {
    if (!user) { navigate("/login"); return; }
    if (myRating < 1) { toast.error("Please select a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${slug}/reviews`, { rating: myRating, comment: myComment });
      toast.success("Thanks for your review!");
      setMyComment(""); setMyRating(0);
      await loadReviews();
      api.get(`/products/${slug}`).then((r) => setProduct(r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <Skeleton className="aspect-square w-full" />
        <div className="space-y-4"><Skeleton className="h-10 w-3/4" /><Skeleton className="h-6 w-1/2" /><Skeleton className="h-32 w-full" /></div>
      </div>
    );
  }

  const specs = Object.entries(product.specs || {});
  const prodImg = product.image && product.image.startsWith("/") ? `https://nc-mycology.com${product.image}` : product.image;
  const productLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name,
    description: product.description || product.short,
    image: prodImg ? [prodImg] : undefined,
    category: product.category,
    brand: { "@type": "Brand", name: "NC Mycology" },
    offers: {
      "@type": "Offer",
      url: `https://nc-mycology.com/product/${product.slug}`,
      priceCurrency: "USD",
      price: Number(product.price || 0).toFixed(2),
      availability: product.in_stock === false || product.stock_count === 0
        ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "NC Mycology" },
    },
    ...(reviews.count > 0 ? {
      aggregateRating: { "@type": "AggregateRating", ratingValue: Number(reviews.average || 0).toFixed(1), reviewCount: reviews.count },
    } : {}),
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <Seo title={product.name} description={product.short} image={product.image} path={`/product/${product.slug}`} type="product">
        <script type="application/ld+json">{JSON.stringify(productLd)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://nc-mycology.com/" },
            { "@type": "ListItem", position: 2, name: "Shop", item: "https://nc-mycology.com/shop" },
            { "@type": "ListItem", position: 3, name: product.category, item: `https://nc-mycology.com/shop?category=${encodeURIComponent(product.category || "")}` },
            { "@type": "ListItem", position: 4, name: product.name, item: `https://nc-mycology.com/product/${product.slug}` },
          ],
        })}</script>
      </Seo>
      <Link to="/shop" className="inline-flex items-center gap-2 font-mono-label text-xs text-muted-foreground hover:text-primary transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Back to shop
      </Link>

      <div className="grid lg:grid-cols-2 gap-px bg-border border border-border">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
          className="relative bg-secondary aspect-square overflow-hidden">
          <img src={product.image || FALLBACK_IMG} alt={product.name} className="w-full h-full object-cover" />
          <span className="absolute top-4 left-4 font-mono-label text-[10px] px-2 py-1 bg-background/80 glass border border-border">
            {product.category}
          </span>
        </motion.div>

        <div className="bg-card p-8 lg:p-12 flex flex-col">
          <div className="flex flex-wrap gap-2 mb-4">
            {(product.badges || []).map((b) => (
              <span key={b} className="inline-flex items-center gap-1 font-mono-label text-[10px] text-primary border border-primary/40 px-2 py-1">
                <Check className="w-3 h-3" /> {b}
              </span>
            ))}
          </div>

          <h1 className="font-display text-4xl lg:text-5xl font-light leading-tight">{product.name}</h1>
          {reviews.count > 0 ? (
            <a href="#reviews" data-testid="rating-summary-link" className="flex items-center gap-2 mt-3 w-fit group">
              <Stars value={reviews.average} size={16} />
              <span className="font-mono text-xs text-muted-foreground group-hover:text-primary transition-colors">{reviews.average} · {reviews.count} review{reviews.count > 1 ? "s" : ""}</span>
            </a>
          ) : (
            <a href="#reviews" data-testid="rating-summary-link" className="inline-flex items-center gap-2 mt-3 w-fit font-mono-label text-[11px] text-muted-foreground hover:text-primary transition-colors">
              <Stars value={0} size={16} /> Be the first to review
            </a>
          )}
          <p className="font-mono text-2xl text-primary mt-4">{money(product.price)}</p>
          <p className="text-muted-foreground leading-relaxed mt-6">{product.description}</p>

          {specs.length > 0 && (
            <div className="mt-8 border border-border divide-y divide-border">
              {specs.map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-3 text-sm">
                  <span className="font-mono-label text-[11px] text-muted-foreground">{k}</span>
                  <span className="font-mono text-xs">{v}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-auto pt-10">
            {product.in_stock === false ? (
              <div data-testid="pdp-out-of-stock" className="border border-destructive/50 bg-destructive/10 text-destructive font-mono-label text-xs h-12 rounded-full flex items-center justify-center">
                Out of Stock
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex items-center border border-border">
                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="pdp-qty-dec"
                    className="w-11 h-12 flex items-center justify-center hover:bg-accent transition-colors">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-12 text-center font-mono" data-testid="pdp-qty">{qty}</span>
                  <button onClick={() => setQty((q) => q + 1)} data-testid="pdp-qty-inc"
                    className="w-11 h-12 flex items-center justify-center hover:bg-accent transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  data-testid="pdp-add-to-cart"
                  onClick={() => { add(product, qty); toast.success(`${product.name} added to cart`); }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-xs h-12 rounded-full transition-[filter,transform] active:scale-[0.98]">
                  <ShoppingBag className="w-4 h-4" /> Add to Cart
                </button>
              </div>
            )}
            {product.type === "digital" && (
              <p className="text-xs text-muted-foreground mt-4 font-mono inline-flex items-center gap-2">
                <Download className="w-3.5 h-3.5 text-primary" /> Digital PDF, instant download in your account after purchase.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section id="reviews" className="mt-16 scroll-mt-28" data-testid="reviews-section">
        <div className="flex items-end justify-between mb-8 border-b border-border pb-4">
          <h2 className="font-display text-3xl font-light">Customer Reviews</h2>
          {reviews.count > 0 && (
            <div className="flex items-center gap-2">
              <Stars value={reviews.average} size={18} />
              <span className="font-mono text-sm text-muted-foreground">{reviews.average} / 5</span>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* Write a review */}
          <div className="lg:col-span-1">
            <div className="border border-border bg-card p-6 sticky top-24">
              <h3 className="font-display text-xl mb-4">Write a review</h3>
              {user ? (
                <>
                  <p className="font-mono-label text-[10px] text-muted-foreground mb-2">Your rating</p>
                  <Stars value={myRating} size={26} onRate={setMyRating} className="mb-4" />
                  <Textarea data-testid="review-comment" value={myComment} onChange={(e) => setMyComment(e.target.value)}
                    placeholder="Share how your grow went..." className="mb-4 min-h-[100px]" />
                  <button onClick={submitReview} disabled={submitting} data-testid="review-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-xs h-11 rounded-full transition-[filter]">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Review"}
                  </button>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  <Link to="/login" className="text-primary hover:underline" data-testid="review-login-link">Sign in</Link> to leave a review.
                </p>
              )}
            </div>
          </div>

          {/* Review list */}
          <div className="lg:col-span-2 space-y-5" data-testid="review-list">
            {reviews.reviews.length === 0 ? (
              <p className="text-muted-foreground py-8">No reviews yet. Be the first to share your experience.</p>
            ) : (
              reviews.reviews.map((r) => (
                <div key={r.id} className="border border-border bg-card p-5" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-lg">{r.user_name}</span>
                      {r.verified && (
                        <span className="inline-flex items-center gap-1 font-mono-label text-[9px] text-primary border border-primary/40 px-1.5 py-0.5">
                          <BadgeCheck className="w-3 h-3" /> Verified Buyer
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <Stars value={r.rating} size={14} />
                  {r.comment && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{r.comment}</p>}
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
